/**
 * Typed API client. All requests are authed with the token in
 * localStorage; a 401 clears the token and forces the caller to
 * re-enter it via the login screen.
 */

import { getToken, setToken } from './auth';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '') + '/api';

// The browser's UTC offset in minutes (Date.getTimezoneOffset()), sent with
// every date-ranged query so the server buckets blocks by the viewer's local
// calendar day instead of the UTC day. Read per-call to stay DST-correct.
function tzOffsetParam(): string {
  return String(new Date().getTimezoneOffset());
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401) {
    setToken(null);
    throw new ApiError(401, 'unauthorized', 'Token rejected');
  }

  if (!res.ok) {
    let code = 'unknown_error';
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.error === 'string') {
        code = body.error;
        message = body.detail ?? body.error;
      }
    } catch {
      // non-JSON error body
    }
    throw new ApiError(res.status, code, message);
  }

  return (await res.json()) as T;
}

// Types — mirror the Worker's contracts (cloudflare/src/contracts/*)

export interface ProjectListEntry {
  id: string;
  project_key: string;
  name: string;
  display_name: string | null;
  client_id: string | null;
  client_name: string | null;
  parent_project_id: string | null;
  parent_project_key: string | null;
  tags: string[];
  standard_rate_cents: number | null;
  human_rate_cents: number | null;
  agent_rate_cents: number | null;
  created_at: string;
  updated_at: string;
  last_activity_at: string | null;
}

export interface Client {
  id: string;
  name: string;
  default_standard_rate_cents: number | null;
  default_human_rate_cents: number | null;
  default_agent_rate_cents: number | null;
  created_at: string;
  updated_at: string;
}

export interface BillingLineItem {
  kind: 'standard' | 'human' | 'agent';
  hours: number;
  rate_cents: number | null;
  amount_cents: number | null;
}

export interface BillingReport {
  project_key: string;
  from: string;
  to: string;
  line_items: BillingLineItem[];
  total_cents: number;
  effective_rates: {
    standard: number | null;
    human: number | null;
    agent: number | null;
    standard_source: 'project' | 'client' | null;
    human_source: 'project' | 'client' | null;
    agent_source: 'project' | 'client' | null;
  };
}

export interface ProjectPatchInput {
  display_name?: string | null;
  client_id?: string | null;
  parent_project_id?: string | null;
  tags?: string[] | null;
  standard_rate_cents?: number | null;
  human_rate_cents?: number | null;
  agent_rate_cents?: number | null;
}

export interface ClientWriteInput {
  name?: string;
  default_standard_rate_cents?: number | null;
  default_human_rate_cents?: number | null;
  default_agent_rate_cents?: number | null;
}

export interface BranchGroupSummary {
  branch: string;
  total_seconds: number;
  human_seconds: number;
  agent_seconds: number;
  block_count: number;
}

export interface ProjectSummary {
  project_key: string;
  project_name: string;
  from: string;
  to: string;
  human_seconds: number;
  agent_labor_seconds: number;
  agent_wall_clock_seconds: number;
  project_wall_clock_seconds: number;
  block_count: number;
  agent_breakdown: Array<{
    agent_name: string;
    labor_seconds: number;
    block_count: number;
  }>;
}

export interface TimeBlock {
  block_id: string;
  project_key: string;
  project_name: string;
  type: 'human' | 'agent';
  agent_name: string | null;
  instance_id: string | null;
  device_id: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  last_seen_at: string | null;
  metadata: string | null;
  updated_at: string;
}

export interface TokenEntry {
  id: string;
  name: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  is_current: boolean;
}

export interface NewToken {
  id: string;
  name: string | null;
  token: string;
  created_at: string;
}

export const api = {
  async listProjects(): Promise<ProjectListEntry[]> {
    const body = await request<{ projects: ProjectListEntry[] }>('/projects');
    return body.projects;
  },

  getProject(projectKey: string): Promise<ProjectListEntry> {
    return request<ProjectListEntry>(`/projects/${encodeURIComponent(projectKey)}`);
  },

  getSummary(projectKey: string, from: string, to: string): Promise<ProjectSummary> {
    const params = new URLSearchParams({ from, to, tzOffset: tzOffsetParam() });
    return request<ProjectSummary>(
      `/projects/${encodeURIComponent(projectKey)}/summary?${params}`
    );
  },

  async listBlocks(projectKey: string, from: string, to: string): Promise<TimeBlock[]> {
    const params = new URLSearchParams({ from, to, tzOffset: tzOffsetParam() });
    const body = await request<{ blocks: TimeBlock[] }>(
      `/projects/${encodeURIComponent(projectKey)}/blocks?${params}`
    );
    return body.blocks;
  },

  async listTokens(): Promise<TokenEntry[]> {
    const body = await request<{ tokens: TokenEntry[] }>('/tokens');
    return body.tokens;
  },

  createToken(name: string | null): Promise<NewToken> {
    return request<NewToken>('/tokens', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  revokeToken(id: string): Promise<void> {
    return request<void>(`/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  async listClients(): Promise<Client[]> {
    const body = await request<{ clients: Client[] }>('/clients');
    return body.clients;
  },

  createClient(input: ClientWriteInput): Promise<Client> {
    return request<Client>('/clients', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  updateClient(id: string, input: ClientWriteInput): Promise<Client> {
    return request<Client>(`/clients/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  deleteClient(id: string): Promise<void> {
    return request<void>(`/clients/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  patchProject(projectKey: string, input: ProjectPatchInput): Promise<ProjectListEntry> {
    return request<ProjectListEntry>(`/projects/${encodeURIComponent(projectKey)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },

  getBilling(projectKey: string, from: string, to: string): Promise<BillingReport> {
    const params = new URLSearchParams({ from, to, tzOffset: tzOffsetParam() });
    return request<BillingReport>(
      `/projects/${encodeURIComponent(projectKey)}/billing?${params}`
    );
  },

  async getBranches(projectKey: string, from: string, to: string): Promise<BranchGroupSummary[]> {
    const params = new URLSearchParams({ from, to, tzOffset: tzOffsetParam() });
    const body = await request<{ branches: BranchGroupSummary[] }>(
      `/projects/${encodeURIComponent(projectKey)}/by-branch?${params}`
    );
    return body.branches;
  },
};
